import { useStore } from '../store/useStore';


/**
 * Returns store data filtered by the current user's role and their active organization.
 * - All users: filtered by currentOrgId (if set) to ensure strict multi-tenant isolation.
 * - Manager: further filtered to only see their category + general categories.
 * - Superadmin & Org Admin: see all data belonging to the active currentOrgId.
 *
 * Write actions (addTeam, updateShift, etc.) are always passed through unfiltered.
 *
 * Special case: shared team (team members linked to multiple orgs via shared_team_organizations)
 * need to see ALL their shifts and points from ALL orgs, not just their primary org.
 */
export function useVisibleStore() {
  const store = useStore();
  const { currentUser, categories, team, points, shifts, issues, messages, currentOrgId, currentProjectId, viewAsManagerCategoryId, sharedTeamOrganizations } = store;

  const currentTeamMember = currentUser.role === 'staff' && currentUser.teamId
    ? team.find(s => s.id === currentUser.teamId)
    : null;
  const teamOrgIds = new Set<string>();
  if (currentTeamMember) {
    for (const link of (sharedTeamOrganizations || [])) {
      if (link.teamId === currentTeamMember.id) teamOrgIds.add(link.orgId);
    }
    for (const s of shifts) {
      if (s.teamId === currentTeamMember.id && s.orgId) teamOrgIds.add(s.orgId);
    }
  }
  const isSharedTeam = currentUser.role === 'staff' && currentUser.teamId !== null && currentTeamMember !== null;

  const filteredCategories = categories.filter(c => 
    (!currentOrgId || c.orgId === currentOrgId) &&
    (!currentProjectId || c.projectId === currentProjectId)
  );

  const filteredTeam = currentOrgId
    ? team.filter(s => 
        s.orgId === currentOrgId || 
        (sharedTeamOrganizations || []).some(link => link.teamId === s.id && link.orgId === currentOrgId)
      )
    : team;

  const filteredPoints = isSharedTeam
    ? points.filter(p => p.orgId && teamOrgIds.has(p.orgId))
    : points.filter(p => 
        (!currentOrgId || p.orgId === currentOrgId) &&
        (!currentProjectId || p.projectId === currentProjectId)
      );

  const filteredShifts = (() => {
    // Category supervisor: sees ALL shifts in their categories
    const isSupervisor = currentTeamMember?.isCategorySupervisor || false;
    const supervisorCategoryIds = isSupervisor
      ? new Set(currentTeamMember?.categoryIds || (currentTeamMember?.categoryId ? [currentTeamMember.categoryId] : []))
      : new Set<string>();
    // Artists: also see all shifts at stage points
    const isArtist = currentTeamMember?.isArtist || false;
    const stagePointIds = isArtist
      ? new Set(points.filter(p => p.isStage).map(p => p.id))
      : new Set<string>();

    if (isSharedTeam) {
      // Team member sees their own shifts + all shifts at the same points (to see colleagues)
      const myShiftPointIds = new Set(
        shifts.filter(s => s.teamId === currentTeamMember?.id).map(s => s.pointId)
      );
      // Also include parent artist's point IDs for sub-members
      if (currentTeamMember?.parentArtistId) {
        shifts.filter(s => s.teamId === currentTeamMember.parentArtistId).forEach(s => myShiftPointIds.add(s.pointId));
      }
      return shifts.filter(s =>
        s.teamId === currentTeamMember?.id ||
        (currentTeamMember?.parentArtistId && s.teamId === currentTeamMember.parentArtistId) ||
        (s.pointId && myShiftPointIds.has(s.pointId)) ||
        (isSupervisor && supervisorCategoryIds.size > 0 && s.pointId && points.some(p => p.id === s.pointId && p.categoryId && supervisorCategoryIds.has(p.categoryId))) ||
        (isArtist && s.pointId && stagePointIds.has(s.pointId))
      );
    }
    return shifts.filter(s =>
      (!currentOrgId || s.orgId === currentOrgId) &&
      (!currentProjectId || s.projectId === currentProjectId)
    );
  })();

  const filteredIssues = issues.filter(i => 
    (!currentOrgId || i.orgId === currentOrgId) &&
    (!currentProjectId || i.projectId === currentProjectId)
  );

  const filteredMessages = (messages || []).filter(m =>
    (!currentOrgId || m.orgId === currentOrgId) &&
    (!currentProjectId || m.projectId === currentProjectId)
  );

  const { approvalRequests } = store;
  const filteredApprovalRequests = (approvalRequests || []).filter(r =>
    (!currentOrgId || r.orgId === currentOrgId) &&
    (!currentProjectId || r.projectId === currentProjectId)
  );

  const effectiveManagerCategoryId = (currentUser.role === 'manager' && currentUser.categoryId) || viewAsManagerCategoryId;

  if (effectiveManagerCategoryId) {
    const myCategory = filteredCategories.find(c => c.id === effectiveManagerCategoryId);

    const visibleCategories = filteredCategories.filter(
      c => c.id === effectiveManagerCategoryId || c.isGeneral
    );
    const visibleCategoryIds = new Set(visibleCategories.map(c => c.id));

    const visibleTeam = filteredTeam.filter(
      s => s.categoryId === effectiveManagerCategoryId
    );
    const visibleTeamIds = new Set(visibleTeam.map(s => s.id));

    const visiblePoints = filteredPoints.filter(
      p => !p.categoryId || visibleCategoryIds.has(p.categoryId)
    );
    const visiblePointIds = new Set(visiblePoints.map(p => p.id));

    const visibleShifts = filteredShifts.filter(
      s => visibleTeamIds.has(s.teamId) || visiblePointIds.has(s.pointId)
    );

    const visibleIssues = filteredIssues.filter(i => visiblePointIds.has(i.pointId));

    const visibleApprovalRequests = filteredApprovalRequests.filter(
      r => r.categoryId === effectiveManagerCategoryId
    );

    return {
      ...store,
      categories: visibleCategories,
      team: visibleTeam,
      points: visiblePoints,
      allPoints: filteredPoints,
      shifts: visibleShifts,
      issues: visibleIssues,
      messages: filteredMessages,
      approvalRequests: visibleApprovalRequests,
      tasks: store.tasks,
      _myCategory: myCategory,
      isEffectiveManager: true,
      effectiveCategoryId: effectiveManagerCategoryId,
    };
  }

  console.log('[VisibleStore] ADMIN: team:', filteredTeam.length, 'points:', filteredPoints.length, 'shifts:', filteredShifts.length, 'categories:', filteredCategories.length, 'currentOrgId:', currentOrgId, 'currentProjectId:', currentProjectId, 'role:', currentUser.role);

  return {
    ...store,
    categories: filteredCategories,
    team: filteredTeam,
    points: filteredPoints,
    allPoints: filteredPoints,
    shifts: filteredShifts,
    issues: filteredIssues,
    messages: filteredMessages,
    approvalRequests: filteredApprovalRequests,
    tasks: store.tasks,
    isEffectiveManager: false,
    effectiveCategoryId: undefined,
  };
}
